import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import DispositionTag from "../components/DispositionTag";
import { DISPOSITION_GROUPS } from "../lib/dispositions";
import { toast } from "sonner";
import { Phone, X, Play, Pause, CalendarCheck, Lock, CalendarClock, RotateCw } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";

const toLocalInputValue = (iso) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? parseISO(iso) : iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const localInputToIso = (v) => (v ? new Date(v).toISOString() : null);

const AudioPlayer = ({ url }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!url)
    return (
      <div className="text-xs text-white/40 font-mono italic">
        No recording available
      </div>
    );
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };
  return (
    <div
      data-testid="call-audio-player"
      className="bg-[#0a0a0b] border border-white/10 rounded-full py-1.5 px-3 flex items-center gap-3"
    >
      <button
        onClick={toggle}
        className="w-7 h-7 rounded-full bg-[#FFB000] hover:bg-[#FFC133] text-black flex items-center justify-center flex-shrink-0"
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex items-center gap-0.5 flex-1 text-[#FFB000]/60">
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={i}
            className="wave-bar"
            style={{ height: `${6 + Math.abs(Math.sin(i * 0.8)) * 14}px` }}
          />
        ))}
      </div>
      <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} />
    </div>
  );
};

const CallDetailPanel = ({ call, onClose, onUpdated }) => {
  const [notes, setNotes] = useState(call.notes || "");
  const [saving, setSaving] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [calendarEventId, setCalendarEventId] = useState("");
  const [followUpLocal, setFollowUpLocal] = useState(toLocalInputValue(call.follow_up_at));
  const [callbackLocal, setCallbackLocal] = useState(
    toLocalInputValue(call.scheduled_callback_at)
  );

  useEffect(() => {
    setNotes(call.notes || "");
    setBookingOpen(false);
    setCalendarEventId("");
    setFollowUpLocal(toLocalInputValue(call.follow_up_at));
    setCallbackLocal(toLocalInputValue(call.scheduled_callback_at));
  }, [call.id, call.follow_up_at, call.scheduled_callback_at]);

  const saveFollowUp = async () => {
    try {
      const { data } = await api.patch(`/calls/${call.id}`, {
        follow_up_at: localInputToIso(followUpLocal),
      });
      toast.success(followUpLocal ? "Follow-up saved" : "Follow-up cleared");
      onUpdated(data);
    } catch {
      toast.error("Could not save follow-up");
    }
  };

  const scheduleCallback = async () => {
    if (!callbackLocal) return toast.error("Pick a date & time");
    try {
      await api.post(`/calls/${call.id}/schedule-callback`, {
        when: localInputToIso(callbackLocal),
      });
      toast.success("Ashley callback scheduled");
      const { data } = await api.get(`/calls/${call.id}`);
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule callback");
    }
  };

  const cancelCallback = async () => {
    try {
      await api.delete(`/calls/${call.id}/schedule-callback`);
      toast.success("Callback cancelled");
      setCallbackLocal("");
      const { data } = await api.get(`/calls/${call.id}`);
      onUpdated(data);
    } catch {
      toast.error("Could not cancel");
    }
  };

  const setDisposition = async (value) => {
    try {
      const { data } = await api.patch(`/calls/${call.id}`, { disposition: value });
      toast.success(`Marked as ${value}`);
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update disposition");
    }
  };

  const confirmBooking = async () => {
    if (!calendarEventId.trim()) {
      return toast.error("Paste the Google Calendar event ID to confirm");
    }
    try {
      const { data } = await api.post(`/calls/${call.id}/confirm-booking`, {
        calendar_event_id: calendarEventId.trim(),
      });
      toast.success("Booking confirmed");
      setBookingOpen(false);
      setCalendarEventId("");
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm booking");
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/calls/${call.id}`, { notes });
      toast.success("Notes saved");
      onUpdated(data);
    } catch {
      toast.error("Could not save notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="call-detail-panel"
      className="fixed inset-y-0 right-0 w-full md:w-[560px] bg-[#0f0f11] border-l border-white/10 z-40 overflow-y-auto shadow-2xl"
    >
      <div className="sticky top-0 bg-[#0f0f11] border-b border-white/10 px-6 py-4 flex items-start justify-between z-10">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">
            Call Detail
          </div>
          <div className="text-lg font-medium text-white">
            {call.prospect_name || "Unknown"}{" "}
            <span className="font-mono text-white/50 text-sm ml-2">{call.prospect_phone}</span>
          </div>
        </div>
        <button
          data-testid="close-call-detail-btn"
          onClick={onClose}
          className="w-8 h-8 rounded-md hover:bg-white/5 text-white/50 flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-6 py-6 space-y-6">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Duration</div>
            <div className="font-mono text-white mt-0.5">{call.duration_seconds || 0}s</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Status</div>
            <div className="font-mono text-white mt-0.5">{call.status}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Ended</div>
            <div className="font-mono text-white/70 mt-0.5 text-xs">{call.ended_reason || "—"}</div>
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2">
            Recording
          </div>
          <AudioPlayer url={call.recording_url} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em]">
              Disposition
            </div>
            {call.ai_disposition && (
              <div className="text-[10px] font-mono text-white/40">
                Ashley suggested:{" "}
                <span className="text-[#FFB000]">{call.ai_disposition}</span>
              </div>
            )}
          </div>

          {call.disposition && (
            <div className="mb-3 text-xs text-white/60">
              Current: <DispositionTag value={call.disposition} />
              {call.calendar_event_id && (
                <span className="ml-2 font-mono text-[10px] text-emerald-400/70">
                  cal_event: {call.calendar_event_id}
                </span>
              )}
            </div>
          )}

          <div className="space-y-3" data-testid="disposition-button-group">
            {DISPOSITION_GROUPS.map((group) => (
              <div key={group.key}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1.5">
                  {group.label}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {group.items.map((item) => {
                    const selected = call.disposition === item.value;
                    const lockedBooked =
                      item.requiresCalendar && !call.calendar_event_id;
                    return (
                      <button
                        key={item.value}
                        title={
                          lockedBooked
                            ? "Booked requires Google Calendar confirmation — use 'Confirm Booking' below"
                            : item.hint || ""
                        }
                        data-testid={`set-disposition-${item.value
                          .replace(/—/g, "-")
                          .replace(/,/g, "")
                          .replace(/\s+/g, "-")
                          .toLowerCase()}`}
                        onClick={() => {
                          if (lockedBooked) {
                            setBookingOpen(true);
                            return;
                          }
                          setDisposition(item.value);
                        }}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors inline-flex items-center gap-1.5 ${
                          selected
                            ? "bg-[#FFB000]/15 border-[#FFB000]/40 text-[#FFB000]"
                            : lockedBooked
                            ? "bg-white/[0.02] border-white/10 text-white/35 hover:text-white/60 cursor-pointer"
                            : "bg-white/[0.03] border-white/10 text-white/60 hover:text-white hover:border-white/20"
                        }`}
                      >
                        {lockedBooked && <Lock className="w-3 h-3" />}
                        {item.value}
                        {item.autoDnc && !selected && (
                          <span className="text-[9px] font-mono text-red-400/60 uppercase tracking-wider">
                            auto-dnc
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Confirm Booking flow — the only way to set Booked */}
          <div className="mt-4 bg-emerald-500/5 border border-emerald-500/20 rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <CalendarCheck className="w-3.5 h-3.5" />
                <span className="font-medium">Confirm Booking</span>
              </div>
              {!bookingOpen && (
                <button
                  data-testid="open-confirm-booking-btn"
                  onClick={() => setBookingOpen(true)}
                  className="text-[11px] font-mono uppercase tracking-wider text-emerald-400 hover:text-emerald-300"
                >
                  Enter event ID →
                </button>
              )}
            </div>
            {bookingOpen && (
              <div className="mt-2.5 space-y-2" data-testid="confirm-booking-form">
                <input
                  data-testid="calendar-event-id-input"
                  value={calendarEventId}
                  onChange={(e) => setCalendarEventId(e.target.value)}
                  placeholder="Google Calendar event ID"
                  className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-xs px-2.5 py-1.5 font-mono placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="confirm-booking-submit-btn"
                    onClick={confirmBooking}
                    className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 font-medium text-xs px-3 py-1.5 rounded-md"
                  >
                    Confirm & mark Booked
                  </button>
                  <button
                    onClick={() => {
                      setBookingOpen(false);
                      setCalendarEventId("");
                    }}
                    className="text-xs text-white/50 hover:text-white px-3 py-1.5"
                  >
                    Cancel
                  </button>
                </div>
                <div className="text-[10px] font-mono text-white/40 leading-relaxed">
                  Phase 1: paste the event ID manually. Phase 4 will auto-populate
                  this when Ashley's book_appointment tool calls Google Calendar.
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 flex items-center gap-2">
            <CalendarClock className="w-3.5 h-3.5" />
            Follow-up & Callback
          </div>
          <div className="space-y-3 bg-[#18181B] border border-white/5 rounded-md p-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">
                Manual follow-up reminder
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="detail-follow-up-at-input"
                  type="datetime-local"
                  value={followUpLocal}
                  onChange={(e) => setFollowUpLocal(e.target.value)}
                  className="flex-1 bg-[#0a0a0b] border border-white/10 rounded-md text-white text-xs px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#FFB000]"
                />
                <button
                  data-testid="detail-save-follow-up-btn"
                  onClick={saveFollowUp}
                  className="bg-white/[0.03] hover:bg-white/[0.08] text-white/80 border border-white/10 text-xs px-3 py-1.5 rounded-md"
                >
                  Save
                </button>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">
                Ashley auto-redial
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="detail-callback-at-input"
                  type="datetime-local"
                  value={callbackLocal}
                  onChange={(e) => setCallbackLocal(e.target.value)}
                  className="flex-1 bg-[#0a0a0b] border border-white/10 rounded-md text-white text-xs px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                />
                {!call.scheduled_callback_at ? (
                  <button
                    data-testid="detail-schedule-callback-btn"
                    onClick={scheduleCallback}
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5"
                  >
                    <RotateCw className="w-3 h-3" /> Schedule
                  </button>
                ) : (
                  <button
                    data-testid="detail-cancel-callback-btn"
                    onClick={cancelCallback}
                    className="bg-white/[0.03] hover:bg-white/[0.08] text-white/60 border border-white/10 text-xs px-3 py-1.5 rounded-md"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {call.scheduled_callback_at && (
                <div className="text-[10px] font-mono text-emerald-400/70 mt-1.5">
                  Ashley will auto-dial on{" "}
                  {format(parseISO(call.scheduled_callback_at), "MMM d · HH:mm")}.{" "}
                  Bypasses the 3-attempt cap. Requires the parent campaign to be
                  running or paused during the calling window.
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2">
            AI Summary
          </div>
          <div className="bg-[#18181B] border border-white/5 rounded-md p-3 text-sm text-white/70 leading-relaxed min-h-[64px]" data-testid="call-summary">
            {call.summary || <span className="italic text-white/30">Summary not available yet.</span>}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2">
            Transcript
          </div>
          <div className="bg-[#18181B] border border-white/5 rounded-md p-3 text-sm text-white/80 font-mono leading-relaxed max-h-[280px] overflow-y-auto whitespace-pre-wrap" data-testid="call-transcript">
            {call.transcript || <span className="italic text-white/30">No transcript yet.</span>}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2">
            Internal Notes
          </div>
          <textarea
            data-testid="call-notes-textarea"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add context for Patrick before the demo…"
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3 py-2 placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
          <button
            data-testid="save-notes-btn"
            onClick={saveNotes}
            disabled={saving}
            className="mt-2 bg-[#18181B] hover:bg-[#27272A] text-white/90 border border-white/10 font-medium text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save notes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Calls() {
  const [calls, setCalls] = useState([]);
  const [filter, setFilter] = useState("");
  const [needsDisposition, setNeedsDisposition] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const params = {};
      if (filter) params.disposition = filter;
      if (needsDisposition) params.needs_disposition = true;
      const { data } = await api.get("/calls", { params });
      setCalls(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, needsDisposition]);

  // Support /calls?open=<call_id> deep-link (used by Follow-ups)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (openId) {
      api
        .get(`/calls/${openId}`)
        .then(({ data }) => setSelected(data))
        .catch(() => {})
        .finally(() => {
          // Clean the query so refreshing doesn't re-open
          navigate("/calls", { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCall = (updated) => {
    setSelected(updated);
    setCalls((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  return (
    <div data-testid="calls-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
          Activity
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-white">Call Log</h1>
        <p className="text-sm text-white/50 mt-2">
          Every call Ashley makes, with transcript, recording, and AI-suggested disposition.
        </p>
      </div>

      <div className="mb-4" data-testid="disposition-filter-group">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="disposition-filter-all"
            onClick={() => {
              setFilter("");
              setNeedsDisposition(false);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              !filter && !needsDisposition
                ? "bg-[#FFB000]/10 border-[#FFB000]/30 text-[#FFB000]"
                : "bg-[#121214] border-white/10 text-white/60 hover:text-white"
            }`}
          >
            All
          </button>
          <button
            data-testid="disposition-filter-needs"
            onClick={() => {
              setNeedsDisposition(true);
              setFilter("");
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              needsDisposition
                ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                : "bg-[#121214] border-white/10 text-white/60 hover:text-white"
            }`}
          >
            Needs Disposition
          </button>
          <select
            data-testid="disposition-filter-select"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setNeedsDisposition(false);
            }}
            className="bg-[#121214] border border-white/10 rounded-md text-white/80 text-xs px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          >
            <option value="">Filter by disposition…</option>
            {DISPOSITION_GROUPS.map((g) => (
              <optgroup key={g.key} label={g.label}>
                {g.items.map((it) => (
                  <option key={it.value} value={it.value}>
                    {it.value}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {filter && (
            <span className="text-[11px] font-mono text-white/50">
              Showing: <DispositionTag value={filter} className="ml-1" />
            </span>
          )}
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="calls-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <th className="text-left px-5 py-3 font-normal">When</th>
                <th className="text-left px-5 py-3 font-normal">Prospect</th>
                <th className="text-left px-5 py-3 font-normal">Phone</th>
                <th className="text-left px-5 py-3 font-normal">Duration</th>
                <th className="text-left px-5 py-3 font-normal">Disposition</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-white/40 text-sm">Loading…</td>
                </tr>
              )}
              {!loading && calls.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-white/40 text-sm">
                    No calls match this filter yet.
                  </td>
                </tr>
              )}
              {calls.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`call-row-${c.id}`}
                  onClick={() => setSelected(c)}
                  className="cursor-pointer hover:bg-white/[0.02]"
                >
                  <td className="px-5 py-3 text-white/70 font-mono text-xs">
                    {c.created_at ? format(new Date(c.created_at), "MMM d · HH:mm") : "—"}
                  </td>
                  <td className="px-5 py-3 text-white/90">{c.prospect_name || "Unknown"}</td>
                  <td className="px-5 py-3 font-mono text-white/70">{c.prospect_phone}</td>
                  <td className="px-5 py-3 font-mono text-white/60">{c.duration_seconds || 0}s</td>
                  <td className="px-5 py-3">
                    <DispositionTag value={c.disposition || c.ai_disposition} />
                  </td>
                  <td className="px-5 py-3 text-white/60">
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider">
                      <Phone
                        className={`w-3 h-3 ${
                          c.status === "ended" ? "text-emerald-400" : "text-[#FFB000] animate-pulse"
                        }`}
                      />
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-30"
            onClick={() => setSelected(null)}
            data-testid="call-detail-backdrop"
          />
          <CallDetailPanel
            call={selected}
            onClose={() => setSelected(null)}
            onUpdated={updateCall}
          />
        </>
      )}
    </div>
  );
}
